// VER-218: Audit-log viewer (Step 11.7). Read-only, consumes GET /console/audit-log
// (keyset-paginated). account_admin sees its own tenant; internal_admin sees all
// accounts (hence the extra Account column for them).
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  App,
  Card,
  Table,
  Tag,
  Button,
  Space,
  Select,
  InputNumber,
  DatePicker,
  Input,
  Typography,
} from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { auditApi, accountApi, brandsApi } from "../../console/consoleApi";
import { useSession } from "../../console/useSession";
import { errorMessage, ROLES } from "../../console/consoleUtils";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Known audit actions (from the Step-4 contract) — a finite set, so a Select beats
// free text. allowClear lets the admin drop back to "all actions".
const ACTIONS = [
  "login.success",
  "login.otp_fail",
  "login.new_ip",
  "invite.sent",
  "invite.accepted",
  "invite.revoked",
  "role.changed",
  "apiperms.changed",
  "preprod.changed",
  "user.removed",
  "domains.changed",
  "account.created",
  "password.reset",
  "apicatalog.added",
  "apicatalog.removed",
];

const PAGE = 25;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function ConsoleAuditLog() {
  const { message } = App.useApp();
  const { user: actor } = useSession();
  const isInternal = actor?.role === ROLES.INTERNAL_ADMIN;

  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null); // nextCursor for "load more"
  const [total, setTotal] = useState(0); // total matching rows on the server
  const [loading, setLoading] = useState(false);

  // Audit rows carry raw accountId/targetId only. Internal admins can list accounts and
  // brands, so resolve ids -> names client-side for the Account and Target columns.
  const [accountMap, setAccountMap] = useState({}); // id -> account name
  const [brandMap, setBrandMap] = useState({}); // id -> brand display name

  // Client-side pagination over the loaded rows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE);

  // Filters
  const [action, setAction] = useState(null);
  const [actorId, setActorId] = useState(null);
  const [range, setRange] = useState(null);
  const [query, setQuery] = useState(""); // client-side text search over loaded rows

  const buildParams = useCallback(
    (nextCursor) => {
      const p = { limit: PAGE };
      if (action) p.action = action;
      if (actorId != null) p.actor = actorId;
      if (range?.[0]) p.from = range[0].toISOString();
      if (range?.[1]) p.to = range[1].toISOString();
      if (nextCursor) p.cursor = nextCursor;
      return p;
    },
    [action, actorId, range]
  );

  // load(false) applies filters from scratch; load(true) appends the next page.
  const load = useCallback(
    async (more = false) => {
      setLoading(true);
      try {
        const res = await auditApi.list(buildParams(more ? cursor : null));
        const { events: rows, nextCursor, total: t } = res.data.data;
        setEvents((prev) => (more ? [...prev, ...rows] : rows));
        setCursor(nextCursor);
        if (t != null) setTotal(t);
      } catch (err) {
        message.error(errorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [buildParams, cursor, message]
  );

  // Initial load only; subsequent loads are driven by Apply / Load more.
  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve account/brand names for the id columns (internal admins only — the endpoints
  // are internal-scoped). Names are best-effort: on failure we keep showing the raw id.
  useEffect(() => {
    if (!isInternal) return;
    let cancelled = false;
    (async () => {
      try {
        const [accRes, brandRes] = await Promise.all([accountApi.listAll(), brandsApi.list()]);
        if (cancelled) return;
        const am = {};
        accRes.data.data.accounts.forEach((a) => (am[a.id] = a.name));
        const bm = {};
        brandRes.data.data.brands.forEach((b) => (bm[b.id] = b.displayName));
        setAccountMap(am);
        setBrandMap(bm);
      } catch {
        // names optional; columns fall back to #id
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInternal]);

  // Client-side text search over the currently-loaded rows (actor / action /
  // target / ip). Server-side filters above narrow the fetch; this refines locally.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) =>
      [e.actorEmail, e.actorName, e.action, e.targetType, e.targetEmail, e.targetName, e.ip]
        .some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [events, query]);

  const columns = [
    { title: "When", dataIndex: "createdAt", key: "createdAt", width: 190, render: fmt },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (a) => <Tag>{a}</Tag>,
    },
    {
      title: "Actor",
      key: "actor",
      render: (_, r) =>
        r.actorEmail || r.actorName ? (
          <span>{r.actorEmail || r.actorName}</span>
        ) : r.actorId != null ? (
          `#${r.actorId}`
        ) : (
          <Text type="secondary">system</Text>
        ),
    },
    {
      title: "Target",
      key: "target",
      render: (_, r) => {
        if (!r.targetType) return "—";
        // User-targeted events resolve to a readable identity; account/brand targets
        // resolve to their name (internal admins); anything unresolved falls back to
        // the raw type + id.
        const person = r.targetEmail || r.targetName;
        if (person) return person;
        if (r.targetId != null) {
          if (r.targetType === "account" && accountMap[r.targetId]) return accountMap[r.targetId];
          if (r.targetType === "brand" && brandMap[r.targetId]) return brandMap[r.targetId];
        }
        return `${r.targetType}${r.targetId != null ? ` #${r.targetId}` : ""}`;
      },
    },
    ...(isInternal
      ? [
          {
            title: "Account",
            dataIndex: "accountId",
            key: "accountId",
            render: (id) => (id != null ? accountMap[id] || `#${id}` : "—"),
          },
        ]
      : []),
    { title: "IP", dataIndex: "ip", key: "ip", render: (ip) => ip || "—" },
  ];

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          Audit log
        </Title>
      }
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => load(false)}>
          Refresh
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Action"
          value={action}
          onChange={setAction}
          options={ACTIONS.map((a) => ({ value: a, label: a }))}
          style={{ width: 200 }}
        />
        <InputNumber
          placeholder="Actor id"
          value={actorId}
          onChange={setActorId}
          style={{ width: 120 }}
          min={1}
        />
        <RangePicker showTime value={range} onChange={setRange} />
        <Button type="primary" onClick={() => load(false)}>
          Apply
        </Button>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search loaded rows"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 220 }}
        />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filtered}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          // "loaded of total on server" — events.length is what we've pulled, `total`
          // is the full matching count. Falls back gracefully if total is unavailable.
          showTotal: () =>
            total ? `${events.length} of ${total} loaded` : `${events.length} loaded`,
          hideOnSinglePage: false,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        expandable={{
          // Show the before/after state captured with the event.
          expandedRowRender: (r) => (
            <Space size="large" align="start" style={{ display: "flex" }}>
              <div>
                <Text strong>Before</Text>
                <pre style={{ margin: 0, maxWidth: 420, overflow: "auto" }}>
                  {r.before ? JSON.stringify(r.before, null, 2) : "—"}
                </pre>
              </div>
              <div>
                <Text strong>After</Text>
                <pre style={{ margin: 0, maxWidth: 420, overflow: "auto" }}>
                  {r.after ? JSON.stringify(r.after, null, 2) : "—"}
                </pre>
              </div>
            </Space>
          ),
          rowExpandable: (r) => Boolean(r.before || r.after),
        }}
      />

      <div style={{ marginTop: 16, textAlign: "center" }}>
        {cursor ? (
          <Button onClick={() => load(true)} loading={loading}>
            Fetch older events
          </Button>
        ) : (
          events.length > 0 && <Text type="secondary">End of log</Text>
        )}
      </div>
    </Card>
  );
}
